import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  ProposalTemplateDto,
  ProposalTemplateOverrides,
} from '@lib/dto/proposal-template.dto'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import { proposalTemplateService } from '@/services/ProposalTemplateService'
import { proposalTemplateKeys, proposalKeys } from '@/hooks/queryKeys'

/**
 * The business's standard proposal wording for one kind of proposal.
 *
 * `proposalId` switches to one proposal's wording, with the organization's
 * set for what it bids layered underneath (`work` is then the server's
 * answer, not the caller's) — the same shape either way, so one editor
 * serves both.
 */
export function useProposalTemplate(
  proposalId?: string,
  work: ProposalWork = 'TREES',
): UseQueryResult<ProposalTemplateDto> {
  return useQuery({
    queryKey:
      proposalId === undefined
        ? proposalTemplateKeys.organization(work)
        : proposalTemplateKeys.proposal(proposalId),
    queryFn: () =>
      proposalId === undefined
        ? proposalTemplateService.get(work)
        : proposalTemplateService.getForProposal(proposalId),
    staleTime: 60_000,
  })
}

/**
 * Saving wording invalidates the documents built from it.
 *
 * Every proposal's rendered document depends on this, and a draft re-renders
 * live — so leaving those cached would show a document that does not match the
 * wording just saved. Sent versions are unaffected whatever happens here:
 * they carry their own frozen copy.
 */
export function useSaveProposalTemplate(
  proposalId?: string,
  work: ProposalWork = 'TREES',
): UseMutationResult<ProposalTemplateDto, Error, ProposalTemplateOverrides> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (overrides: ProposalTemplateOverrides) =>
      proposalId === undefined
        ? proposalTemplateService.save(work, overrides)
        : proposalTemplateService.saveForProposal(proposalId, overrides),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: proposalTemplateKeys.all })
      void queryClient.invalidateQueries({ queryKey: proposalKeys.all })
    },
  })
}

/**
 * Render the sample document with wording that has not been saved.
 *
 * A mutation rather than a query, because it POSTs a body and its result is
 * asked for rather than kept — caching a preview keyed by a document's worth
 * of prose would be a cache nothing ever hits.
 */
export function usePreviewProposalTemplate(
  proposalId?: string,
  work: ProposalWork = 'TREES',
): UseMutationResult<ProposalDocumentDto, Error, ProposalTemplateOverrides> {
  return useMutation({
    mutationFn: (overrides: ProposalTemplateOverrides) =>
      proposalId === undefined
        ? proposalTemplateService.preview(work, overrides)
        : proposalTemplateService.previewForProposal(proposalId, overrides),
  })
}
